import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  FileText, Grid3X3, BookOpen, Upload, Users,
  FolderOpen, ArrowRight, ArrowUpRight, Send,
  CheckCircle, XCircle, BarChart3, Loader2, Inbox,
  UserCheck, Database, Zap, UserPlus, Newspaper,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatRow = ({ label, value, color }) => (
  <div className="flex items-center justify-between py-[3px]">
    <div className="flex items-center gap-2">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${color}`} />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <span className="text-xs font-semibold text-gray-700 tabular-nums">{value ?? 0}</span>
  </div>
);

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const AdminDashboard = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [submissionLoading, setSubmissionLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_URL}/api/admin/content/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) setStats(await response.json());
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await fetch(`${API_URL}/api/admin/content/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setRecentSubmissions(Array.isArray(data) ? data.slice(0, 5) : []);
        }
      } catch (err) {
        console.error('Failed to fetch submissions:', err);
      } finally {
        setSubmissionLoading(false);
      }
    };
    fetchSubmissions();
  }, []);

  const handleQuickReview = async (submissionId, action) => {
    setReviewingId(submissionId);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/content/submissions/${submissionId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: action }),
      });
      if (res.ok) {
        toast({ title: action === 'approved' ? 'Approved' : 'Rejected', description: `Submission ${action}.` });
        setRecentSubmissions(prev => prev.filter(s => s.id !== submissionId));
        setStats(prev => prev
          ? { ...prev, submissions_pending: Math.max(0, (prev.submissions_pending || 0) - 1) }
          : prev
        );
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to review submission', variant: 'destructive' });
    } finally {
      setReviewingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return iso; }
  };

  const quickActions = [
    { name: 'Create Template', icon: FileText, link: '/admin/templates/new', color: 'bg-violet-600 hover:bg-violet-700' },
    { name: 'Upload Element', icon: Grid3X3, link: '/admin/elements', color: 'bg-blue-600 hover:bg-blue-700' },
    { name: 'Write Blog Post', icon: BookOpen, link: '/admin/blog/new', color: 'bg-emerald-600 hover:bg-emerald-700' },
    { name: 'Upload Asset', icon: Upload, link: '/admin/assets', color: 'bg-pink-600 hover:bg-pink-700' },
    { name: 'Invite Designer', icon: UserPlus, link: '/admin/designers', color: 'bg-amber-600 hover:bg-amber-700' },
  ];

  const pendingCount = stats?.submissions_pending || 0;

  return (
    <AdminLayout>
      <div className="space-y-6 w-full">

        {/* ── Header + Quick Actions ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Welcome back — here's what's happening on iVisora.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {quickActions.map(action => {
              const Icon = action.icon;
              return (
                <Link key={action.name} to={action.link}>
                  <Button size="sm" className={`${action.color} text-white text-xs gap-1.5 shadow-sm`}>
                    <Icon className="w-3.5 h-3.5" />
                    {action.name}
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── Pending Approvals Alert ── */}
        {(loading || pendingCount > 0) && (
          <div className={`rounded-xl border-2 p-4 flex items-center justify-between gap-4
            ${loading ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Inbox className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                {loading ? (
                  <>
                    <Skeleton className="h-4 w-52 mb-1.5" />
                    <Skeleton className="h-3 w-40" />
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-amber-900">
                      {pendingCount} submission{pendingCount !== 1 ? 's' : ''} awaiting approval
                    </p>
                    <p className="text-sm text-amber-700">
                      Designer submissions need your review before going live
                    </p>
                  </>
                )}
              </div>
            </div>
            <Link to="/admin/submissions">
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white gap-1.5 shadow-sm flex-shrink-0">
                Review Now
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        )}

        {/* ── Primary Stat Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

          {/* Templates */}
          <Link to="/admin/templates">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group h-full">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-violet-500 transition-colors mt-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900 tabular-nums">
                  {loading ? <Skeleton className="h-7 w-14 inline-block" /> : (stats?.templates?.total ?? 0)}
                </p>
                <p className="text-sm text-gray-500 font-medium mt-0.5">Total Templates</p>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-0.5">
                  <StatRow label="Published (Live)" value={stats?.templates?.published} color="bg-emerald-400" />
                  <StatRow label="Pending Review"   value={stats?.templates?.pending}   color="bg-amber-400" />
                  <StatRow label="Draft"            value={stats?.templates?.drafts}    color="bg-gray-300" />
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Elements */}
          <Link to="/admin/elements">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group h-full">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Grid3X3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors mt-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900 tabular-nums">
                  {loading ? <Skeleton className="h-7 w-14 inline-block" /> : (stats?.elements ?? 0)}
                </p>
                <p className="text-sm text-gray-500 font-medium mt-0.5">Elements</p>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-0.5">
                  <StatRow label="Icons" value={stats?.elements_by_type?.icon} color="bg-blue-400" />
                  <StatRow label="Stickers" value={stats?.elements_by_type?.sticker} color="bg-pink-400" />
                  <StatRow label="Shapes" value={stats?.elements_by_type?.shape} color="bg-violet-400" />
                  <StatRow label="Graphics" value={stats?.elements_by_type?.image} color="bg-emerald-400" />
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Blog Posts */}
          <Link to="/admin/blog">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group h-full">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Newspaper className="w-5 h-5 text-emerald-600" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 transition-colors mt-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900 tabular-nums">
                  {loading ? <Skeleton className="h-7 w-14 inline-block" /> : (stats?.blog_posts?.total ?? 0)}
                </p>
                <p className="text-sm text-gray-500 font-medium mt-0.5">Blog Posts</p>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-0.5">
                  <StatRow label="Published" value={stats?.blog_posts?.published} color="bg-emerald-400" />
                  <StatRow label="Draft" value={stats?.blog_posts?.drafts} color="bg-gray-300" />
                  <StatRow label="Scheduled" value={stats?.blog_posts?.scheduled ?? 0} color="bg-blue-400" />
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Categories + Assets stacked */}
          <div className="col-span-1 flex flex-col gap-4">
            <Link to="/admin/categories" className="flex-1">
              <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors flex-shrink-0">
                    <FolderOpen className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 tabular-nums">
                      {loading ? '—' : (stats?.categories ?? 0)}
                    </p>
                    <p className="text-xs text-gray-500 font-medium">Categories</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/assets" className="flex-1">
              <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-pink-100 flex items-center justify-center group-hover:bg-pink-200 transition-colors flex-shrink-0">
                    <Database className="w-4 h-4 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 tabular-nums">
                      {loading ? '—' : (stats?.assets ?? 0)}
                    </p>
                    <p className="text-xs text-gray-500 font-medium">Assets</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* ── Secondary Stat Row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/users">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {loading ? '—' : (stats?.users ?? stats?.admins ?? 0)}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">Total Users</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/designers">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                  <UserCheck className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {loading ? '—' : (stats?.designers ?? 0)}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">Active Designers</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/submissions">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                  <Send className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">
                    {loading ? '—' : pendingCount}
                  </p>
                  <p className="text-sm text-gray-500 font-medium">Pending Approvals</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/analytics">
            <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group bg-gradient-to-br from-violet-600 to-fuchsia-600 border-0">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">View Analytics</p>
                  <p className="text-xs text-white/70 mt-0.5">Platform insights →</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* ── Bottom Row: Submissions Widget + Quick Start ── */}
        <div className="grid lg:grid-cols-3 gap-6">

          {/* Recent Submissions Widget */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Inbox className="w-4 h-4 text-amber-500" />
                Recent Submissions
              </CardTitle>
              <Link to="/admin/submissions">
                <Button variant="ghost" size="sm" className="text-xs text-violet-600 hover:text-violet-700 h-7 px-2 gap-1">
                  View all
                  <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-0">
              {submissionLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-2">
                      <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-44" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : recentSubmissions.length === 0 ? (
                <div className="py-10 text-center">
                  <Inbox className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 font-medium">No pending submissions</p>
                  <p className="text-xs text-gray-400 mt-0.5">Submissions from designers will appear here</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentSubmissions.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-violet-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {s.name || 'Untitled'}
                        </p>
                        <p className="text-xs text-gray-500">
                          by {s.submitted_by_name || 'Unknown'} · {formatDate(s.submitted_at)}
                        </p>
                      </div>
                      <Badge className={`text-[11px] flex-shrink-0 border-0 ${
                        s.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        s.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {s.status || 'pending'}
                      </Badge>
                      {(!s.status || s.status === 'pending') && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <Button
                            size="sm"
                            disabled={reviewingId === s.id}
                            className="h-7 w-7 p-0 bg-emerald-500 hover:bg-emerald-600 text-white"
                            onClick={() => handleQuickReview(s.id, 'approved')}
                            title="Approve"
                          >
                            {reviewingId === s.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <CheckCircle className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            disabled={reviewingId === s.id}
                            className="h-7 w-7 p-0 bg-red-500 hover:bg-red-600 text-white"
                            onClick={() => handleQuickReview(s.id, 'rejected')}
                            title="Reject"
                          >
                            {reviewingId === s.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <XCircle className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Start Guide */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Zap className="w-4 h-4 text-violet-500" />
                Quick Start
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1">
              {[
                { step: '1', label: 'Create Categories', desc: 'Organize templates and elements', link: '/admin/categories', stepColor: 'bg-violet-50 text-violet-600 border-violet-200' },
                { step: '2', label: 'Upload Elements', desc: 'Add shapes, icons & stickers', link: '/admin/elements', stepColor: 'bg-blue-50 text-blue-600 border-blue-200' },
                { step: '3', label: 'Build Templates', desc: 'Create design templates for users', link: '/admin/templates/new', stepColor: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
                { step: '4', label: 'Invite Designers', desc: 'Grow your content team', link: '/admin/designers', stepColor: 'bg-amber-50 text-amber-600 border-amber-200' },
                { step: '5', label: 'Write Blog Posts', desc: 'Share design tips & tutorials', link: '/admin/blog/new', stepColor: 'bg-pink-50 text-pink-600 border-pink-200' },
              ].map(item => (
                <Link key={item.step} to={item.link} className="block">
                  <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group">
                    <div className={`w-7 h-7 rounded-lg border flex items-center justify-center text-xs font-bold flex-shrink-0 ${item.stepColor}`}>
                      {item.step}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </div>

      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
